import { defineSchema } from '../../schema-meta.ts';
import { registerCategorySchema } from '../../registry.ts';

/**
 * Server-side enemy catalogue. Used by:
 *   - calcScore — `enemyScores: { id, score }[]` per the score formula
 *   - worker damage feasibility — `health` (HP we had to chip down) + `atk`
 *     (how much damage the enemy can do once it reaches the player)
 *
 * Per-wave multipliers (`healthMultiplier`, `atkMultiplier`, `spawnInterval`)
 * stay inline on `stage.waves[].enemies[]` etc. — they vary by wave; this
 * catalogue holds the base stats only.
 *
 * Client-only display fields (name, resource_id, attack_patterns, movement
 * trajectory etc.) are intentionally absent — the server doesn't render
 * enemies and the catalogue stays small.
 */
export const EnemySchema = defineSchema({
  name: 'serverEnemies',
  fields: [
    ['id', 'number'],
    ['score', 'number'],
    ['health', 'number'],
    ['atk', 'number'],
    ['speed', 'number'],
  ],
  lookupKeys: ['id'],
});

export interface RawEnemyRow {
  id: number;
  score: number;
  health: number;
  atk: number;
  speed: number;
}

export function enemyFromRaw(row: RawEnemyRow) {
  return {
    id: row.id,
    score: Number(row.score ?? 0),
    health: Number(row.health ?? 0),
    atk: Number(row.atk ?? 0),
    speed: Number(row.speed ?? 0),
  };
}

registerCategorySchema('serverEnemies', EnemySchema);
